/**
 * VendorInviteAccept Component
 * 
 * Public page for vendors to accept invites and create accounts.
 * This is a one-time onboarding flow - vendors do NOT use organization codes.
 * 
 * Flow:
 * 1. Read token from URL query param
 * 2. Fetch invite details (GET /api/vendors/invites/accept/:token)
 * 3. Display invite info and password form
 * 4. Submit acceptance (POST /api/vendors/invites/accept/:token)
 * 5. Store tokens and redirect to workflows
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Mail, Lock, Building2, Calendar, AlertCircle, CheckCircle } from 'lucide-react';

// Storage keys (matching AuthContext)
const TOKEN_KEY = 'asset_track_token';
const REFRESH_TOKEN_KEY = 'asset_track_refresh_token';
const USER_KEY = 'asset_track_user';

export default function VendorInviteAccept() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  const { isDark } = useTheme();
  const navigate = useNavigate();

  // Fetch invite details on mount
  useEffect(() => {
    const fetchInvite = async () => {
      if (!token) {
        setError('Invalid invite link. No token provided.');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/api/vendors/invites/accept/${token}`);
        
        if (response.isValid && response.canAccept) {
          setInvite(response.invite);
        } else {
          setError('This invite is no longer valid.');
        }
      } catch (err) {
        // Handle different error statuses
        if (err.status === 404) {
          setError('Invalid or expired invite.');
        } else if (err.status === 410) {
          setError('This invite has expired or is no longer valid.');
        } else {
          setError('Failed to load invite. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInvite();
  }, [token]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error on input change
  };

  const validateForm = () => {
    if (!formData.password) {
      setError('Password is required.');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await api.post(`/api/vendors/invites/accept/${token}`, {
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });

      if (response.success) {
        // Store tokens and user data (matching AuthContext pattern)
        localStorage.setItem(TOKEN_KEY, response.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(response.user));
        
        // Set token in API client for subsequent requests
        api.setToken(response.token);

        // Redirect to workflows (vendor dashboard)
        // Use window.location to trigger full page reload so AuthContext reinitializes
        window.location.href = '/workflows';
      } else {
        setError(response.message || 'Failed to accept invite. Please try again.');
      }
    } catch (err) {
      // Handle specific error statuses
      if (err.status === 400) {
        setError(err.data?.message || 'Invalid password. Please check your input.');
      } else if (err.status === 409) {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (err.status === 410) {
        setError('This invite has expired or is no longer valid.');
      } else if (err.status === 404) {
        setError('Invalid or expired invite.');
      } else {
        setError(err.data?.message || 'Failed to accept invite. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Format expiration date for display
  const formatExpirationDate = (expiresAt) => {
    if (!expiresAt) return 'Unknown';
    const date = new Date(expiresAt);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if invite expires soon (within 24 hours)
  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const expiration = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiration = (expiration - now) / (1000 * 60 * 60);
    return hoursUntilExpiration > 0 && hoursUntilExpiration <= 24;
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 ${
      isDark ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100'
    }`}>
      {/* Background pattern */}
      <div className={`absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI1MmIiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0zMCAzNHYtNGgtMnY0aDJ6bTAtNnYtNGgtMnY0aDJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] ${isDark ? 'opacity-20' : 'opacity-5'}`}></div>
      
      <div className="relative w-full max-w-2xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/aegislogo.png" alt="AEGIS Logo" className="w-20 h-20 mx-auto mb-4" />
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Accept Vendor Invite
          </h1>
          <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Complete your account setup
          </p>
        </div>

        {/* Main Card */}
        <div className={`backdrop-blur-xl rounded-2xl border shadow-2xl p-8 ${
          isDark ? 'bg-gray-800/50 border-gray-700/50' : 'bg-white/80 border-gray-200'
        }`}>
          
          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Loading invite details...
              </p>
            </div>
          )}

          {/* Error State (No Invite) */}
          {!loading && !invite && error && (
            <div className="text-center py-8">
              <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Invalid Invite
              </h3>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {error}
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Please contact the organization that invited you for a new invite link.
              </p>
            </div>
          )}

          {/* Invite Details and Form */}
          {!loading && invite && (
            <>
              {/* Invite Information */}
              <div className={`mb-6 p-4 rounded-lg border ${
                isDark ? 'bg-blue-900/20 border-blue-800/50' : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    isDark ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <div className="flex-1">
                    <p className={`text-sm font-medium mb-2 ${
                      isDark ? 'text-blue-300' : 'text-blue-900'
                    }`}>
                      You've been invited by <span className="font-semibold">{invite.customerOrganizationName}</span>
                    </p>
                    <div className={`space-y-1 text-xs ${
                      isDark ? 'text-blue-400' : 'text-blue-700'
                    }`}>
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3" />
                        <span>{invite.vendorEmail}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3 h-3" />
                        <span>{invite.vendorCompanyName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3 h-3" />
                        <span>Expires: {formatExpirationDate(invite.expiresAt)}</span>
                      </div>
                    </div>
                    {isExpiringSoon(invite.expiresAt) && (
                      <p className={`mt-2 text-xs font-medium ${
                        isDark ? 'text-yellow-400' : 'text-yellow-700'
                      }`}>
                        ⚠️ This invite expires soon. Please complete your account setup now.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Password Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  <Lock size={20} />
                  Create Your Password
                </h3>

                {/* Password */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="••••••••"
                    minLength={8}
                    required
                    className={`w-full px-4 py-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500'
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Must be at least 8 characters
                  </p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    placeholder="••••••••"
                    required
                    className={`w-full px-4 py-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500'
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Creating account...
                    </span>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <p className={`mt-8 text-center text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
